import React, { useState, useEffect } from 'react';
import { FileDown, Building2, Calendar, IndianRupee, ChevronRight, ChevronLeft, Plus, Trash2, CheckCircle } from 'lucide-react';
import ExcelJS from 'exceljs';
import DateInput from './DateInput';
import './AppointmentOrderForm.css';

// Mock data - matching Fom_X.xlsx template placeholders exactly
const mockFormData = {
  // Establishment field
  est_name: "ABC Manufacturing Pvt Ltd",

  // Date fields
  pay_date: "2025-01-31",
  wages_from_date: "2025-01-01",
  wages_to_date: "2025-01-31",

  // Employee array (each employee appears in alternating rows)
  employees: [
    {
      emp_name: "Rajesh",
      emp_lastname: "Kumar",
      emp_desig: "Production Supervisor",
      emp_atnd_worked: "26",
      emp_ot_worked: "8",
      emp_hr: "200",
      emp_basic: "25000",
      emp_da: "3000",
      emp_min_wages: "22000",
      emp_basic_paid: "25000",
      emp_da_paid: "3000",
      emp_gross_paybl: "28000",
      emp_oted: "0",
      emp_ded: "0",
      emp_pf_cont: "1800",
      emp_tot_ded: "1800",
      emp_tot_wg_paid: "26200",
      emp_wage_paid: "26200"
    },
    {
      emp_name: "Priya",
      emp_lastname: "Sharma",
      emp_desig: "Quality Inspector",
      emp_atnd_worked: "26",
      emp_ot_worked: "4",
      emp_hr: "180",
      emp_basic: "22000",
      emp_da: "2500",
      emp_min_wages: "20000",
      emp_basic_paid: "22000",
      emp_da_paid: "2500",
      emp_gross_paybl: "24500",
      emp_oted: "0",
      emp_ded: "0",
      emp_pf_cont: "1650",
      emp_tot_ded: "1650",
      emp_tot_wg_paid: "22850",
      emp_wage_paid: "22850"
    }
  ]
};

const FormXRegisterWages = () => {
  const [formData, setFormData] = useState(mockFormData);
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    // Fetch data from API on component mount
    const fetchFormData = async () => {
      try {
        console.log('Fetching Form X data from API...');
        // const response = await fetch('/api/form-x-data');
        // const data = await response.json();
        // setFormData(data);
        console.log('Using mock data for Form X');
      } catch (error) {
        console.error('Error fetching Form X data:', error);
        console.log('Falling back to mock data');
      }
    };

    fetchFormData();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEmployeeChange = (index, field, value) => {
    const updatedEmployees = [...formData.employees];
    updatedEmployees[index] = {
      ...updatedEmployees[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      employees: updatedEmployees
    }));
  };

  const addEmployee = () => {
    const newEmployee = {
      emp_name: "",
      emp_lastname: "",
      emp_desig: "",
      emp_atnd_worked: "",
      emp_ot_worked: "",
      emp_hr: "",
      emp_basic: "",
      emp_da: "",
      emp_min_wages: "",
      emp_basic_paid: "",
      emp_da_paid: "",
      emp_gross_paybl: "",
      emp_oted: "",
      emp_ded: "",
      emp_pf_cont: "",
      emp_tot_ded: "",
      emp_tot_wg_paid: "",
      emp_wage_paid: ""
    };
    setFormData(prev => ({
      ...prev,
      employees: [...prev.employees, newEmployee]
    }));
  };

  const removeEmployee = (index) => {
    if (formData.employees.length > 1) {
      const updatedEmployees = formData.employees.filter((_, idx) => idx !== index);
      setFormData(prev => ({ ...prev, employees: updatedEmployees }));
      // If we're on a step beyond the new employee count, go back
      if (currentStep > updatedEmployees.length) {
        setCurrentStep(updatedEmployees.length);
      }
    }
  };

  const nextStep = () => {
    const totalSteps = 1 + formData.employees.length; // 1 (organization) + employees
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const generateExcel = async () => {
    setIsGenerating(true);

    try {
      console.log('Starting Form X Excel generation...');
      console.log('Form Data:', formData);

      // Load the template
      const response = await fetch('/templates/Fom_X.xlsx');
      const arrayBuffer = await response.arrayBuffer();

      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);
      const worksheet = workbook.worksheets[0];

      console.log('Template loaded successfully');

      // Remove ALL background fills from the entire worksheet
      worksheet.eachRow({ includeEmpty: true }, (row) => {
        row.eachCell({ includeEmpty: true }, (cell) => {
          if (cell.fill) {
            cell.fill = null;
          }
        });
      });

      console.log('Background fills removed');

      // Two-pass placeholder replacement strategy

      // Pass 1: Replace form-level fields (organization, dates)
      console.log('Pass 1: Replacing form-level fields...');
      worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
        row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
          const cellValue = cell.value ? String(cell.value) : '';

          if (cellValue.includes('{{')) {
            const placeholderRegex = /\{\{\s*([^}]+?)\s*\}\}/g;
            let newValue = cellValue;
            let match;

            while ((match = placeholderRegex.exec(cellValue)) !== null) {
              const fieldName = match[1].trim();
              const normalizedFieldName = fieldName.replace(/\s+/g, '_');

              // Check form-level fields
              if (formData[normalizedFieldName] !== undefined) {
                console.log(`Row ${rowNumber}, Col ${colNumber}: Replacing {{ ${fieldName} }} with "${formData[normalizedFieldName]}"`);
                newValue = newValue.replace(match[0], formData[normalizedFieldName]);
              }
            }

            if (newValue !== cellValue) {
              cell.value = newValue;
            }
          }
        });
      });

      // Pass 2: Replace employee-specific fields for the first employee
      console.log('Pass 2: Replacing employee fields...');
      if (formData.employees && formData.employees.length > 0) {
        const employee = formData.employees[0];

        worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
          row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            const cellValue = cell.value ? String(cell.value) : '';

            if (cellValue.includes('{{')) {
              const placeholderRegex = /\{\{\s*([^}]+?)\s*\}\}/g;
              let newValue = cellValue;
              let match;

              while ((match = placeholderRegex.exec(cellValue)) !== null) {
                const fieldName = match[1].trim();
                const normalizedFieldName = fieldName.replace(/\s+/g, '_');

                // Check employee fields
                if (employee[normalizedFieldName] !== undefined) {
                  console.log(`Row ${rowNumber}, Col ${colNumber}: Replacing {{ ${fieldName} }} with "${employee[normalizedFieldName]}"`);
                  newValue = newValue.replace(match[0], employee[normalizedFieldName]);
                }
              }

              if (newValue !== cellValue) {
                cell.value = newValue;
              }
            }
          });
        });
      }

      console.log('All placeholders replaced');

      // Generate Form file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Form-X-Register-Wages-${formData.employees[0]?.emp_name || 'Employee'}-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log('Excel file generated and downloaded successfully');

      setTimeout(() => {
        setIsGenerating(false);
        alert('Form X - Register of Wages Excel generated successfully!');
      }, 500);

    } catch (error) {
      console.error('Error generating Excel:', error);
      alert(`Error generating Excel: ${error.message}`);
      setIsGenerating(false);
    }
  };

  // Calculate total steps: 1 (organization) + employees
  const totalSteps = 1 + formData.employees.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  // Render step content
  const renderStepContent = () => {
    // Step 0: Organization & Period
    if (currentStep === 0) {
      return (
        <div className="form-section">
          <div className="section-header">
            <Building2 size={20} />
            <h3>Organization & Wage Period</h3>
          </div>
          <div className="form-grid">
            <div className="form-field full-width">
              <label>Organization Name</label>
              <input
                type="text"
                value={formData.est_name}
                onChange={(e) => handleInputChange('est_name', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Payment Date</label>
              <DateInput
                value={formData.pay_date}
                onChange={(e) => handleInputChange('pay_date', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Wages From Date</label>
              <DateInput
                value={formData.wages_from_date}
                onChange={(e) => handleInputChange('wages_from_date', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Wages To Date</label>
              <DateInput
                value={formData.wages_to_date}
                onChange={(e) => handleInputChange('wages_to_date', e.target.value)}
              />
            </div>
          </div>
        </div>
      );
    }

    // Steps 1 to N: Employee details
    if (currentStep > 0 && currentStep <= formData.employees.length) {
      const employeeIndex = currentStep - 1;
      const employee = formData.employees[employeeIndex];

      return (
        <div className="form-section" style={{ background: 'var(--theme-surface)', border: '1px solid #e5e7eb' }}>
          <div className="section-header" style={{ justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <IndianRupee size={20} />
              <h3>Employee {employeeIndex + 1} - Wage Details</h3>
            </div>
            {formData.employees.length > 1 && (
              <button
                onClick={() => removeEmployee(employeeIndex)}
                style={{
                  background: '#ef4444',
                  color: 'var(--theme-surface, #ffffff)',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '0.5rem 1rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem'
                }}
              >
                <Trash2 size={16} />
                Remove Employee
              </button>
            )}
          </div>

          {/* Basic Information */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ fontSize: '0.95rem', fontWeight: '600', marginBottom: '1rem', color: '#374151' }}>
              Basic Information
            </h4>
            <div className="form-grid">
              <div className="form-field">
                <label>Employee Name</label>
                <input
                  type="text"
                  value={employee.emp_name}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_name', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Last Name</label>
                <input
                  type="text"
                  value={employee.emp_lastname}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_lastname', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Designation</label>
                <input
                  type="text"
                  value={employee.emp_desig}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_desig', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Attendance & Rates */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ fontSize: '0.95rem', fontWeight: '600', marginBottom: '1rem', color: '#374151' }}>
              Attendance & Rates
            </h4>
            <div className="form-grid">
              <div className="form-field">
                <label>Days Worked</label>
                <input
                  type="number"
                  value={employee.emp_atnd_worked}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_atnd_worked', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>OT Hours Worked</label>
                <input
                  type="number"
                  value={employee.emp_ot_worked}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_ot_worked', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Hourly Rate</label>
                <input
                  type="number"
                  value={employee.emp_hr}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_hr', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Minimum Wages</label>
                <input
                  type="number"
                  value={employee.emp_min_wages}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_min_wages', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Wage Components */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ fontSize: '0.95rem', fontWeight: '600', marginBottom: '1rem', color: '#374151' }}>
              Wage Components
            </h4>
            <div className="form-grid">
              <div className="form-field">
                <label>Basic Wage</label>
                <input
                  type="number"
                  value={employee.emp_basic}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_basic', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>DA</label>
                <input
                  type="number"
                  value={employee.emp_da}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_da', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Basic Paid</label>
                <input
                  type="number"
                  value={employee.emp_basic_paid}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_basic_paid', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>DA Paid</label>
                <input
                  type="number"
                  value={employee.emp_da_paid}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_da_paid', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Gross Payable</label>
                <input
                  type="number"
                  value={employee.emp_gross_paybl}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_gross_paybl', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Deductions & Net Pay */}
          <div>
            <h4 style={{ fontSize: '0.95rem', fontWeight: '600', marginBottom: '1rem', color: '#374151' }}>
              Deductions & Net Pay
            </h4>
            <div className="form-grid">
              <div className="form-field">
                <label>OT Deduction</label>
                <input
                  type="number"
                  value={employee.emp_oted}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_oted', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Other Deductions</label>
                <input
                  type="number"
                  value={employee.emp_ded}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_ded', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>PF Contribution</label>
                <input
                  type="number"
                  value={employee.emp_pf_cont}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_pf_cont', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Total Deductions</label>
                <input
                  type="number"
                  value={employee.emp_tot_ded}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_tot_ded', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Total Wages Paid</label>
                <input
                  type="number"
                  value={employee.emp_tot_wg_paid}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_tot_wg_paid', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Wage Paid</label>
                <input
                  type="number"
                  value={employee.emp_wage_paid}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_wage_paid', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Add Another Employee Button */}
          <button
            onClick={addEmployee}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              backgroundColor: '#10b981',
              color: 'var(--theme-surface, #ffffff)',
              border: 'none',
              borderRadius: '6px',
              padding: '0.75rem 1.5rem',
              fontSize: '0.95rem',
              cursor: 'pointer',
              marginTop: '1.5rem'
            }}
          >
            <Plus size={20} />
            Add Another Employee
          </button>
        </div>
      );
    }
  };

  return (
    <div className="appointment-order-container">
      <div className="appointment-header">
        <div>
          <h2 className="appointment-title">Form X - Register of Wages</h2>
          <p className="appointment-subtitle">Statutory Register for Recording Wages Paid</p>
        </div>
      </div>

      <div className="appointment-content">
        {/* Progress Bar */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Step {currentStep + 1} of {totalSteps}
            </span>
            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              {currentStep === 0 ? 'Organization & Period' : `Employee ${currentStep}`}
            </span>
          </div>
          <div style={{ width: '100%', height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{
              width: `${progress}%`,
              height: '100%',
              background: 'linear-gradient(to right, #3b82f6, #8b5cf6)',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>

        {/* Step Content */}
        {renderStepContent()}

        {/* Navigation Buttons */}
        <div className="form-actions" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem' }}>
          <button
            onClick={prevStep}
            disabled={currentStep === 0}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              background: currentStep === 0 ? '#f8f9fa' : 'var(--theme-surface, #ffffff)',
              color: currentStep === 0 ? '#9ca3af' : '#374151',
              cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: '500'
            }}
          >
            <ChevronLeft size={20} />
            Previous
          </button>

          {currentStep === totalSteps - 1 ? (
            <button
              onClick={generateExcel}
              disabled={isGenerating}
              className={`btn-generate ${isGenerating ? 'generating' : ''}`}
            >
              {isGenerating ? (
                <>
                  <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" opacity="0.25"/>
                    <path d="M12 2a10 10 0 0 1 0 20" opacity="0.75"/>
                  </svg>
                  <span>Generating Excel...</span>
                </>
              ) : (
                <>
                  <FileDown size={20} />
                  <span>Generate Form</span>
                </>
              )}
            </button>
          ) : (
            <button
              onClick={nextStep}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                borderRadius: '6px',
                border: 'none',
                background: 'linear-gradient(to right, #3b82f6, #8b5cf6)',
                color: 'var(--theme-surface, #ffffff)',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: '500'
              }}
            >
              Next
              <ChevronRight size={20} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormXRegisterWages;
